/**
 * POS shift store binding (EG-369 — ADR-011).
 *
 * Cashier (JWT.sid set) → BE auto-resolves store from JWT, FE không cần inject.
 * Owner/manager (no sid) → mở ca POS phải chọn store; FE persist storeId vào
 * sessionStorage và axios interceptor auto-inject `?store_id=...` cho mọi
 * request `/pos/*` cho đến khi đóng ca.
 *
 * Session-scope (không persist sang tab khác / sau logout) — buộc owner mở ca
 * lại mỗi browser session để tránh stale binding.
 */
import { useSyncExternalStore } from 'react';

export const POS_SHIFT_STORE_ID_KEY = 'pos.shiftStoreId';
/**
 * Tenant tag đi kèm `pos.shiftStoreId`. Dùng để phát hiện stale binding khi
 * owner/manager đổi tài khoản (tenant khác) trong cùng tab mà không qua Logout
 * → tránh inject store_id của tenant cũ sang request /pos/* (403 STORE_ACCESS_DENIED).
 */
export const POS_SHIFT_TENANT_ID_KEY = 'pos.shiftStoreTenantId';
const POS_SHIFT_EVENT = 'pos-shift-store-changed';

function emitChange(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(POS_SHIFT_EVENT));
  } catch {
    /* swallow */
  }
}

export function getPosShiftStoreId(): string | null {
  try {
    return sessionStorage.getItem(POS_SHIFT_STORE_ID_KEY);
  } catch {
    // sessionStorage không khả dụng (SSR / private mode)
    return null;
  }
}

export function setPosShiftStoreId(storeId: string): void {
  try {
    sessionStorage.setItem(POS_SHIFT_STORE_ID_KEY, storeId);
    emitChange();
  } catch {
    /* swallow */
  }
}

export function clearPosShiftStoreId(): void {
  try {
    sessionStorage.removeItem(POS_SHIFT_STORE_ID_KEY);
    sessionStorage.removeItem(POS_SHIFT_TENANT_ID_KEY);
    emitChange();
  } catch {
    /* swallow */
  }
}

export function getPosShiftStoreTenantId(): string | null {
  try {
    return sessionStorage.getItem(POS_SHIFT_TENANT_ID_KEY);
  } catch {
    return null;
  }
}

export function setPosShiftStoreTenantId(tenantId: string): void {
  try {
    sessionStorage.setItem(POS_SHIFT_TENANT_ID_KEY, tenantId);
  } catch {
    /* swallow */
  }
}

/**
 * React hook subscribe vào pos.shiftStoreId changes — fires khi setPosShiftStoreId
 * hoặc clearPosShiftStoreId chạy ở bất kỳ component nào trong cùng tab. Cũng
 * lắng nghe `storage` event để cross-tab sync (best-effort).
 *
 * Dùng thay vì useState để 2 components (TopBar StoreSelector + POS sidebar
 * dropdown) auto-sync khi 1 trong 2 thay đổi store.
 */
export function usePosShiftStoreId(): string | null {
  return useSyncExternalStore(
    (callback) => {
      const handler = () => callback();
      window.addEventListener(POS_SHIFT_EVENT, handler);
      window.addEventListener('storage', handler);
      return () => {
        window.removeEventListener(POS_SHIFT_EVENT, handler);
        window.removeEventListener('storage', handler);
      };
    },
    () => getPosShiftStoreId(),
    () => null,
  );
}

/**
 * EG-369: tự động set `pos.shiftStoreId` vào sessionStorage tại login khi
 * có thể infer được store duy nhất:
 *   - Cashier có user.storeId (sid from JWT) → dùng storeId đó
 *   - User có đúng 1 store_assignment → dùng store đó
 *   - User có deliveryStoreId (owner default store) → dùng deliveryStoreId
 *   - Còn lại (multi-assignment, no fallback) → no-op; user phải mở ca qua /pos/shifts
 *
 * Idempotent: nếu sessionStorage đã có value (user đã mở ca trước đó), không ghi đè.
 *
 * Tenant-scoped guard: nếu binding hiện tại được gắn tenant KHÁC với user đang
 * đăng nhập (đổi tài khoản cùng tab) → coi là stale, xoá và re-init theo user mới.
 * KHÔNG validate theo storeAssignments: owner FE chỉ giữ vài assignment nhưng được
 * BE cho phép mọi store của tenant → validate ∈ assignments sẽ xoá nhầm ca hợp lệ.
 */
export function autoInitPosShiftStore(user: {
  tenantId?: string;
  storeId?: string;
  storeAssignments?: string[];
  deliveryStoreId?: string;
}): void {
  const existing = getPosShiftStoreId();
  if (existing) {
    if (!user.tenantId) return; // không có tenant context → giữ idempotent
    const boundTenant = getPosShiftStoreTenantId();
    if (boundTenant && boundTenant !== user.tenantId) {
      clearPosShiftStoreId(); // stale cross-tenant → xoá rồi re-init bên dưới
    } else {
      // Cùng tenant (hoặc binding cũ chưa có tag) → giữ nguyên, backfill tag.
      if (!boundTenant) setPosShiftStoreTenantId(user.tenantId);
      return;
    }
  }

  const bind = (storeId: string): void => {
    setPosShiftStoreId(storeId);
    if (user.tenantId) setPosShiftStoreTenantId(user.tenantId);
  };

  if (user.storeId) {
    bind(user.storeId);
    return;
  }

  const assignments = user.storeAssignments ?? [];
  if (assignments.length === 1) {
    bind(assignments[0]);
    return;
  }

  // Owner case: chưa có store_assignments nhưng có delivery_store_id (default store).
  if (assignments.length === 0 && user.deliveryStoreId) {
    bind(user.deliveryStoreId);
    return;
  }

  // Multi-assignment or no fallback → no-op
}

export interface StoreResolutionOpts {
  /** Cashier sid lock (User.storeId từ JWT). */
  userStoreId?: string | null;
  /** Store mặc định của user (User.delivery_store_id ?? default_store_id) — mirror BE. */
  deliveryStoreId?: string | null;
}

/**
 * Nguồn DUY NHẤT cho "store đang hiệu lực" — dùng chung cho display
 * (StoreSelector.currentStore) và persist, để "store hiển thị" == "store gửi BE".
 *
 * Thứ tự fallback mirror pattern toàn app (Exchange/BuyBack) và semantic BE
 * (`effective_store_id`): shift ?? user.storeId ?? deliveryStoreId ?? stores[0].
 * Mọi ứng viên phải ∈ `availableStoreIds` (store user thực sự truy cập được).
 */
export function resolveActiveStoreId(
  shiftStoreId: string | null | undefined,
  availableStoreIds: string[],
  opts?: StoreResolutionOpts,
): string | null {
  if (shiftStoreId && availableStoreIds.includes(shiftStoreId)) return shiftStoreId;
  const { userStoreId, deliveryStoreId } = opts ?? {};
  if (userStoreId && availableStoreIds.includes(userStoreId)) return userStoreId;
  if (deliveryStoreId && availableStoreIds.includes(deliveryStoreId)) return deliveryStoreId;
  return availableStoreIds[0] ?? null;
}

/**
 * EG-369 follow-up: giữ "store hiển thị trên header" == "store gửi xuống BE".
 *
 * Owner đa store: `autoInitPosShiftStore` no-op (nhiều assignment) nên
 * `pos.shiftStoreId` rỗng. StoreSelector khi đó hiển thị store qua fallback
 * NHƯNG axios interceptor không inject `?store_id=` (vì shiftStoreId rỗng) → BE
 * scope theo `default_store_id` (store khác) → INSTANT trả tồn = 0 dù header
 * hiển thị store có tồn.
 *
 * Trả storeId cần persist (chính là store đang hiển thị), hoặc `null` khi:
 *   - đã có shift binding (không ghi đè lựa chọn tường minh của user), hoặc
 *   - chưa có store nào để chọn.
 */
export function resolvePosShiftStoreToPersist(
  shiftStoreId: string | null | undefined,
  availableStoreIds: string[],
  opts?: StoreResolutionOpts,
): string | null {
  // Binding còn hợp lệ → giữ nguyên lựa chọn tường minh của user.
  if (shiftStoreId && availableStoreIds.includes(shiftStoreId)) return null;
  // TCR-1694: binding STALE (chi nhánh vừa bị gỡ khỏi `store_assignments`) rơi
  // xuống đây cùng nhánh "chưa có binding" — phải bind lại theo store đang hiển
  // thị, nếu không interceptor vẫn gắn `?store_id=<cũ>` → 403 hàng loạt.
  return resolveActiveStoreId(null, availableStoreIds, opts);
}

/**
 * TCR-1694 — binding trong sessionStorage trỏ tới chi nhánh user KHÔNG còn
 * truy cập được (bị gỡ khỏi `store_assignments` khi đang mở POS).
 *
 * Tách khỏi `resolvePosShiftStoreToPersist` vì hai trạng thái cần xử lý khác
 * nhau: còn chi nhánh khác thì bind lại, KHÔNG còn chi nhánh nào thì phải XOÁ
 * binding (hàm resolve trả `null` cho cả hai nên không phân biệt được).
 */
export function isPosShiftStoreBindingStale(
  shiftStoreId: string | null | undefined,
  availableStoreIds: string[],
): boolean {
  if (!shiftStoreId) return false;
  return !availableStoreIds.includes(shiftStoreId);
}
